import { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useDrag } from "@use-gesture/react";
import { BookmarkSimple, PencilSimple } from "@phosphor-icons/react";
import { applyIngredientMultiplier } from "../utils/mergeShoppingIngredients";
import { TAG_COLORS } from "../constants/tagColors";
import recipePlaceholderBg from "../assets/recipe-placeholder-bg.svg";

function RecipeDetailsModal({
  isOpen,
  allowQuickAdd,
  modalOverlayStyle,
  closeRecipeModal,
  modalContentStyle,
  isMobile,
  selectedRecipe,
  closeButtonStyle,
  hoveredButton,
  setHoveredButton,
  selectedPlanSlots,
  programmeMealEditTarget,
  mealSlotSheetTarget,
  applyRecipeToProgrammeMeal,
  applyMealToSlot,
  addRecipeToPlanSelectedSlots,
  goToRecipePlanView,
  secondaryButtonStyle,
  handleEditSelectedRecipe,
  handleDeleteSelectedRecipe,
  recipeModalView,
  recipeModalDirection,
  goToRecipeDetailsView,
  days,
  plan,
  toggleRecipePlanSlot,
  confirmAddSelectedRecipeToPlan,
  recipeNotes,
  setRecipeNotes,
  primaryButtonStyle,
  appTheme,
  setPlanToastMessage,
  setPlan,
  getDateKey,
  getPlanCalendarDays,
  planCalendarWeekOffset,
  fullPage,
  completeMeal,
  completedMeals,
  fullPageContext,
  favouriteRecipes,
  toggleFavourite,
  openMealSlotSheet,
  removeFromPlan,
  openManualEntryModal,
  servingSizePreference,
  onStartCooking,
}) {
  const [activeTab, setActiveTab] = useState("ingredients");
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddSlots, setQuickAddSlots] = useState([]);
  const [recipeNoteDraft, setRecipeNoteDraft] = useState("");
  const [showMenu, setShowMenu] = useState(false);
  const [currentRecipeServings, setCurrentRecipeServings] = useState(servingSizePreference ?? 1);
  const [showCopiedToast, setShowCopiedToast] = useState(false);
  const [quickAddWeekOffset, setQuickAddWeekOffset] = useState(0);

  useEffect(() => {
    setActiveTab("ingredients");
    setShowQuickAdd(false);
    setQuickAddSlots([]);
    setQuickAddWeekOffset(0);
    setCurrentRecipeServings(servingSizePreference ?? 1);
    setRecipeNoteDraft("");
  }, [selectedRecipe?.name, servingSizePreference]);

  useEffect(() => {
    if (activeTab === "notes") {
      setRecipeNoteDraft(recipeNotes?.[selectedRecipe?.name] || "");
    }
  }, [activeTab, recipeNotes, selectedRecipe?.name]);

  // ── Gesture state ──────────────────────────────────────────────────────────
  const sheetRef      = useRef(null);
  const scrollRef     = useRef(null);
  const actionBarRef  = useRef(null);
  const sheetYRef     = useRef(0);
  const peekRef       = useRef(window.innerHeight * 0.65);
  const expandedRef   = useRef(window.innerHeight * 0.25);
  const snappedRef    = useRef("peek");
  const [actionBarHeight, setActionBarHeight] = useState(120);

  // Recalculate offsets and reset sheet whenever recipe/fullPage changes
  useLayoutEffect(() => {
    if (!fullPage) return;
    peekRef.current     = window.innerHeight * 0.65;
    expandedRef.current = window.innerHeight * 0.25;

    const measureAndReset = () => {
      if (actionBarRef.current) {
        const h = actionBarRef.current.offsetHeight;
        if (h > 0) setActionBarHeight(h);
      }
      if (sheetRef.current) {
        const y = peekRef.current;
        sheetRef.current.style.transition = "none";
        sheetRef.current.style.transform  = `translateY(${y}px)`;
        sheetYRef.current  = y;
        snappedRef.current = "peek";
      }
      if (scrollRef.current) {
        scrollRef.current.style.touchAction = "none";
      }
    };

    // Small delay so DOM has painted and action bar has measurable height
    const id = requestAnimationFrame(measureAndReset);
    return () => cancelAnimationFrame(id);
  }, [fullPage, selectedRecipe?.name]);

  // Shared snap helper — updates transform, refs, and scroll container touchAction
  function snapSheetTo(targetY) {
    if (!sheetRef.current) return;
    sheetRef.current.style.transition = "transform 0.2s ease-out";
    sheetRef.current.style.transform  = `translateY(${targetY}px)`;
    sheetYRef.current  = targetY;
    snappedRef.current = targetY <= expandedRef.current ? "expanded" : "peek";
    if (scrollRef.current) {
      scrollRef.current.style.touchAction = snappedRef.current === "peek" ? "none" : "pan-y";
    }
  }

  // Drag handle + tab bar zone
  const bind = useDrag(
    ({ movement: [, my], velocity: [, vy], direction: [, dy], last, memo }) => {
      if (!sheetRef.current) return memo;
      const peek     = peekRef.current;
      const expanded = expandedRef.current;
      const startY   = memo ?? sheetYRef.current;
      const newY     = Math.max(expanded, Math.min(peek, startY + my));
      if (!last) {
        sheetRef.current.style.transition = "none";
        sheetRef.current.style.transform  = `translateY(${newY}px)`;
      } else {
        const flickDown = vy > 0.3 && dy > 0;
        const flickUp   = vy > 0.3 && dy < 0;
        const mid       = (expanded + peek) / 2;
        snapSheetTo(flickDown ? peek : flickUp ? expanded : newY < mid ? expanded : peek);
      }
      return startY;
    },
    { filterTaps: true, threshold: 8, axis: "y" }
  );

  // Scroll content zone — handles peek→expanded and expanded@top→peek
  const bindScroll = useDrag(
    ({ movement: [, my], velocity: [, vy], direction: [, dy], last, memo }) => {
      if (!sheetRef.current) return memo;
      const peek      = peekRef.current;
      const expanded  = expandedRef.current;
      const startY    = memo ?? sheetYRef.current;
      const scrollTop = scrollRef.current?.scrollTop ?? 0;
      const snapped   = snappedRef.current;

      // Peek + swiping up → expand
      if (snapped === "peek" && dy < 0) {
        const newY = Math.max(expanded, Math.min(peek, startY + my));
        if (!last) {
          sheetRef.current.style.transition = "none";
          sheetRef.current.style.transform  = `translateY(${newY}px)`;
        } else {
          const flickUp = vy > 0.3 && dy < 0;
          const mid     = (expanded + peek) / 2;
          snapSheetTo(flickUp || newY < mid ? expanded : peek);
        }
        return startY;
      }

      // Expanded + at top + swiping down → peek
      if (snapped === "expanded" && scrollTop <= 0 && dy > 0) {
        const newY = Math.max(expanded, Math.min(peek, startY + my));
        if (!last) {
          sheetRef.current.style.transition = "none";
          sheetRef.current.style.transform  = `translateY(${newY}px)`;
        } else {
          const flickDown = vy > 0.3 && dy > 0;
          const mid       = (expanded + peek) / 2;
          snapSheetTo(flickDown || newY > mid ? peek : expanded);
        }
        return startY;
      }

      // All other cases: native scroll handles it, don't interfere
      return startY;
    },
    { filterTaps: true, threshold: 4, axis: "y" }
  );

  // ── Guards ─────────────────────────────────────────────────────────────────
  if (!isOpen) return null;

  // ── Shared values ──────────────────────────────────────────────────────────
  const sheetWeekDays    = getPlanCalendarDays ? getPlanCalendarDays(quickAddWeekOffset) : [];
  const todayKey         = new Date().toISOString().split("T")[0];
  const addToPlanWeekLabel = ["This week", "Next week", "In 2 weeks", "In 3 weeks", "In 4 weeks"][quickAddWeekOffset];
  const isCustomRecipe   = !!selectedRecipe?.custom;

  const hasDescription = !!selectedRecipe?.description?.trim();
  const hasTime        = selectedRecipe?.time !== "" && selectedRecipe?.time !== null && selectedRecipe?.time !== undefined;
  const hasCalories    = selectedRecipe?.calories !== "" && selectedRecipe?.calories !== null && selectedRecipe?.calories !== undefined;
  const hasProtein     = selectedRecipe?.macros?.protein !== "" && selectedRecipe?.macros?.protein !== null && selectedRecipe?.macros?.protein !== undefined;
  const hasCarbs       = selectedRecipe?.macros?.carbs   !== "" && selectedRecipe?.macros?.carbs   !== null && selectedRecipe?.macros?.carbs   !== undefined;
  const hasFats        = selectedRecipe?.macros?.fats    !== "" && selectedRecipe?.macros?.fats    !== null && selectedRecipe?.macros?.fats    !== undefined;
  const hasAnyMacros   = hasProtein || hasCarbs || hasFats;
  const hasInstructions = !!selectedRecipe?.instructions?.trim();

  const fpDateKey  = fullPageContext?.dateKey || (fullPageContext?.isProgramme ? `week${fullPageContext?.weekIndex}-${fullPageContext?.day}` : null);
  const fpMealType = fullPageContext?.mealType;
  const isCooked   = completedMeals?.[`${fpDateKey}-${fpMealType}`] || false;
  const hasNote    = !!recipeNotes?.[selectedRecipe?.name];

  const ACCENT = appTheme.accentColor;

  const parseSteps = (text) => {
    if (!text?.trim()) return [];
    const lines = text.split("\n").filter((l) => l.trim());
    const steps = [];
    let current = null;
    for (const line of lines) {
      const numbered = line.trim().match(/^(\d+)[.)]\s+(.+)/);
      if (numbered) {
        if (current) steps.push(current);
        current = { title: numbered[2].trim(), body: "" };
      } else if (current) {
        current.body = current.body ? current.body + " " + line.trim() : line.trim();
      } else {
        steps.push({ title: "", body: line.trim() });
      }
    }
    if (current) steps.push(current);
    if (!steps.length) return [{ title: "", body: text.trim() }];
    return steps;
  };

  const parsedSteps = parseSteps(selectedRecipe?.instructions);

  const pillStyle = {
    padding: "6px 12px", borderRadius: "999px", fontSize: "12px", fontWeight: "500",
    background: appTheme.pillBackground, border: appTheme.pillBorder,
    color: appTheme.secondaryText, display: "flex", alignItems: "center", gap: "4px",
  };

  async function handleShare() {
    const shareData = { title: selectedRecipe?.name, text: selectedRecipe?.description || "", url: window.location.href };
    try {
      if (navigator.share) { await navigator.share(shareData); }
      else {
        await navigator.clipboard.writeText(window.location.href);
        setShowCopiedToast(true);
        setTimeout(() => setShowCopiedToast(false), 2000);
      }
    } catch { /* user cancelled or clipboard failed */ }
  }

  // ── Shared JSX blocks ──────────────────────────────────────────────────────

  // Macro bar (liquid glass, used in both layouts)
  const macroBar = (!isCustomRecipe || hasAnyMacros) ? (
    <div>
      <div style={{
        display: "flex", borderRadius: "16px",
        background: "rgba(0,0,0,0.45)",
        backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)",
        border: "1px solid rgba(255,255,255,0.15)",
        boxShadow: "0 4px 24px rgba(0,0,0,0.08)", overflow: "hidden",
      }}>
        {[
          { label: "Protein", raw: hasProtein ? selectedRecipe?.macros?.protein : isCustomRecipe ? null : 0 },
          { label: "Carbs",   raw: hasCarbs   ? selectedRecipe?.macros?.carbs   : isCustomRecipe ? null : 0 },
          { label: "Fats",    raw: hasFats    ? selectedRecipe?.macros?.fats    : isCustomRecipe ? null : 0 },
        ].map(({ label, raw }, i) => {
          const value = raw !== null && raw !== undefined ? Math.round(raw) : null;
          return (
            <div key={label} style={{ flex: 1, padding: "12px 8px", textAlign: "center", borderLeft: i > 0 ? "1px solid rgba(255,255,255,0.2)" : "none" }}>
              <div style={{ fontSize: "15px", fontWeight: "700", color: "#ffffff" }}>{value !== null ? `${value}g` : "—"}</div>
              <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.7)", marginTop: "2px" }}>{label}</div>
            </div>
          );
        })}
      </div>
      {!isCustomRecipe && (
        <div style={{ fontSize: "10px", color: "rgba(255,255,255,0.75)", textAlign: "center", letterSpacing: "0.01em", marginTop: "6px", marginBottom: "6px" }}>
          Nutritional values are estimates only.
        </div>
      )}
    </div>
  ) : null;

  // Tab bar (shared)
  const tabBar = (
    <div style={{
      display: "flex", borderBottom: "1px solid rgba(0,0,0,0.08)",
      padding: "0 20px", background: appTheme.pageBackground, flexShrink: 0,
    }}>
      {[
        { key: "ingredients", label: "Ingredients" },
        ...(!isCustomRecipe || hasInstructions ? [{ key: "steps", label: "Steps" }] : []),
        { key: "notes",       label: hasNote ? "Notes ●" : "Notes" },
      ].map(({ key, label }) => (
        <button
          key={key}
          onClick={() => setActiveTab(key)}
          style={{
            flex: 1, background: "none", border: "none",
            borderBottom: activeTab === key ? `2.5px solid ${ACCENT}` : "2.5px solid transparent",
            cursor: "pointer", padding: "14px 0 11px", fontSize: "14px",
            fontWeight: activeTab === key ? "700" : "400",
            color: activeTab === key ? appTheme.primaryText : appTheme.secondaryText,
            marginBottom: "-1px", transition: "all 0.15s ease",
          }}
        >
          {label}
        </button>
      ))}
    </div>
  );

  // Tab content (shared)
  const tabContent = (
    <div style={{
      padding: "16px 20px 24px",
      animation: recipeModalDirection === "forward"
        ? "slideFadeInForward 0.22s cubic-bezier(0.22, 1, 0.36, 1)"
        : "slideFadeInBack 0.22s cubic-bezier(0.22, 1, 0.36, 1)",
    }}>
      {/* INGREDIENTS */}
      {activeTab === "ingredients" && (
        <div>
          {(selectedRecipe.ingredients || []).length === 0 ? (
            <div style={{ color: appTheme.secondaryText, fontSize: "14px", textAlign: "center", padding: "32px 0" }}>
              No ingredients added yet.
            </div>
          ) : (
            (selectedRecipe.ingredients || []).map((ingredient, i) => {
              const isLast = i === (selectedRecipe.ingredients?.length ?? 0) - 1;
              return (
                <div key={i} style={{ padding: "11px 0", borderBottom: isLast ? "none" : "1px solid rgba(0,0,0,0.05)" }}>
                  <span style={{ fontSize: "14px", color: appTheme.primaryText }}>
                    {typeof ingredient === "object" && ingredient !== null ? (
                      currentRecipeServings === 1 ? (
                        <>
                          {ingredient.quantity && <span style={{ color: appTheme.mutedText, marginRight: "5px" }}>{ingredient.quantity}</span>}
                          {ingredient.name}
                        </>
                      ) : (
                        applyIngredientMultiplier([ingredient.quantity, ingredient.name].filter(Boolean).join(" "), currentRecipeServings)
                      )
                    ) : (
                      applyIngredientMultiplier(ingredient, currentRecipeServings)
                    )}
                  </span>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* STEPS */}
      {activeTab === "steps" && (
        <div>
          {(!isCustomRecipe || hasInstructions) ? (
            parsedSteps.length > 0 ? (
              parsedSteps.map((step, i) => (
                <div key={i} style={{ display: "flex", gap: "14px", marginBottom: "22px" }}>
                  <div style={{ width: "32px", height: "32px", borderRadius: "50%", background: ACCENT, color: "#fff", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px", fontWeight: "700" }}>
                    {i + 1}
                  </div>
                  <div style={{ paddingTop: "5px" }}>
                    {step.title && <div style={{ fontWeight: "700", fontSize: "14px", color: appTheme.primaryText, marginBottom: step.body ? "5px" : "0" }}>{step.title}</div>}
                    {step.body  && <div style={{ fontSize: "13px", color: appTheme.secondaryText, lineHeight: 1.65 }}>{step.body}</div>}
                  </div>
                </div>
              ))
            ) : (
              <div style={{ color: appTheme.secondaryText, fontSize: "14px", textAlign: "center", padding: "32px 0" }}>No steps available.</div>
            )
          ) : (
            <div style={{ color: appTheme.secondaryText, fontSize: "14px", textAlign: "center", padding: "32px 0" }}>Add cooking steps when editing this recipe.</div>
          )}
        </div>
      )}

      {/* NOTES */}
      {activeTab === "notes" && (
        <div>
          <textarea
            value={recipeNoteDraft}
            onChange={(e) => setRecipeNoteDraft(e.target.value)}
            placeholder="Add your own notes — adaptations, tips, what worked well..."
            rows={6}
            style={{ width: "100%", boxSizing: "border-box", padding: "14px", borderRadius: "16px", border: appTheme.accentBorder, background: appTheme.inputBackground, fontSize: "14px", color: appTheme.primaryText, lineHeight: 1.6, resize: "none", outline: "none", fontFamily: appTheme.fontBody, marginBottom: "10px" }}
          />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
            <span style={{ fontSize: "12px", color: appTheme.secondaryText }}>
              {recipeNoteDraft !== (recipeNotes[selectedRecipe?.name] || "") ? "Unsaved changes" : hasNote ? "Saved" : ""}
            </span>
            <div style={{ display: "flex", gap: "8px" }}>
              {hasNote && (
                <button onClick={() => { setRecipeNotes(selectedRecipe.name, ""); setRecipeNoteDraft(""); }} style={{ padding: "7px 14px", borderRadius: "999px", border: appTheme.actionBorder || "1px solid rgba(230,84,76,0.35)", background: appTheme.actionLight || "rgba(230,84,76,0.12)", color: appTheme.actionColor || "#E6544C", fontSize: "12px", fontWeight: "600", cursor: "pointer" }}>Clear</button>
              )}
              <button onClick={() => { const t = recipeNoteDraft.trim(); if (!t) { setRecipeNotes(selectedRecipe.name, ""); return; } setRecipeNotes(selectedRecipe.name, t); }} style={{ padding: "7px 14px", borderRadius: "999px", border: appTheme.accentBorder, background: appTheme.accentLight, color: appTheme.accentColor, fontSize: "12px", fontWeight: "700", cursor: "pointer" }}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Quick-add sheet (shared between both render paths)
  const quickAddSheet = allowQuickAdd && showQuickAdd ? (
    <div style={{ position: "fixed", inset: 0, zIndex: 1700, display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "center" }}>
      <div style={{ position: "absolute", inset: 0, background: "rgba(15,23,42,0.45)", backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)" }} onClick={() => { setShowQuickAdd(false); setQuickAddSlots([]); setQuickAddWeekOffset(0); }} />
      <div style={{ position: "relative", width: "100%", maxWidth: "560px", borderRadius: "24px 24px 0 0", background: appTheme.pageBackground, boxShadow: "0 -8px 40px rgba(15,23,42,0.16)", animation: "settingsSheetIn 0.28s cubic-bezier(0.22, 1, 0.36, 1)", display: "flex", flexDirection: "column", maxHeight: "82vh" }}>
        <div style={{ width: "36px", height: "4px", borderRadius: "2px", background: "rgba(0,0,0,0.15)", margin: "10px auto 0", flexShrink: 0 }} />
        <div style={{ display: "flex", alignItems: "center", padding: "12px 20px 8px", flexShrink: 0 }}>
          <div style={{ flex: 1, fontSize: "17px", fontWeight: "600", fontFamily: appTheme.fontHeading, color: appTheme.primaryText }}>Add to plan</div>
          <button onClick={() => { setShowQuickAdd(false); setQuickAddSlots([]); setQuickAddWeekOffset(0); }} style={{ width: "30px", height: "30px", borderRadius: "999px", border: appTheme.pillBorder, background: appTheme.pillBackground, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1 1l10 10M11 1L1 11" stroke={appTheme.secondaryText} strokeWidth="1.8" strokeLinecap="round"/></svg>
          </button>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 20px 12px", flexShrink: 0 }}>
          <button onClick={() => setQuickAddWeekOffset((o) => Math.max(0, o - 1))} disabled={quickAddWeekOffset === 0} style={{ width: "32px", height: "32px", borderRadius: "999px", border: appTheme.pillBorder, background: quickAddWeekOffset === 0 ? "transparent" : appTheme.pillBackground, cursor: quickAddWeekOffset === 0 ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", opacity: quickAddWeekOffset === 0 ? 0.3 : 1, flexShrink: 0, outline: "none" }}>
            <svg width="7" height="12" viewBox="0 0 7 12" fill="none"><path d="M6 1L1 6l5 5" stroke={appTheme.primaryText} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
          <div style={{ fontSize: "14px", fontWeight: "600", color: appTheme.primaryText }}>{addToPlanWeekLabel}</div>
          <button onClick={() => setQuickAddWeekOffset((o) => Math.min(4, o + 1))} disabled={quickAddWeekOffset === 4} style={{ width: "32px", height: "32px", borderRadius: "999px", border: appTheme.pillBorder, background: quickAddWeekOffset === 4 ? "transparent" : appTheme.pillBackground, cursor: quickAddWeekOffset === 4 ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", opacity: quickAddWeekOffset === 4 ? 0.3 : 1, flexShrink: 0, outline: "none" }}>
            <svg width="7" height="12" viewBox="0 0 7 12" fill="none"><path d="M1 1l5 5-5 5" stroke={appTheme.primaryText} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
        </div>
        <div className="modal-scroll-hidden" style={{ overflowY: "auto", flex: 1, padding: "0 20px 4px" }}>
          {sheetWeekDays.map((date) => {
            const dk = getDateKey(date);
            const isPast = dk < todayKey;
            const isToday = dk === todayKey;
            const parts = date.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }).split(" ");
            return (
              <div key={dk} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "9px 0", borderBottom: "1px solid rgba(0,0,0,0.05)", opacity: isPast ? 0.38 : 1 }}>
                <div style={{ width: "82px", flexShrink: 0 }}>
                  <div style={{ fontSize: "12px", fontWeight: "700", color: appTheme.primaryText, lineHeight: 1.25 }}>{parts[0]}</div>
                  <div style={{ fontSize: "11px", color: appTheme.secondaryText, lineHeight: 1.2 }}>{parts.slice(1).join(" ")}</div>
                  {isToday && <div style={{ fontSize: "9px", fontWeight: "700", color: appTheme.accentColor, background: appTheme.accentLight, padding: "1px 5px", borderRadius: "4px", display: "inline-block", marginTop: "2px", letterSpacing: "0.03em" }}>TODAY</div>}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "6px", flex: 1 }}>
                  {["breakfast", "lunch", "dinner"].map((mealType) => {
                    const currentMeal = plan[dk]?.[mealType];
                    const isSelected = quickAddSlots.some((s) => s.dateKey === dk && s.mealType === mealType);
                    const isAdded    = !isSelected && currentMeal?.name === selectedRecipe?.name;
                    const isReplace  = !isSelected && !isAdded && !!currentMeal;
                    let bg, border, color, label;
                    if (isSelected)      { bg = appTheme.accentColor; border = appTheme.accentBorder; color = "#ffffff"; label = "✓"; }
                    else if (isAdded)    { bg = "rgba(220,252,231,0.7)"; border = "1px solid rgba(134,239,172,0.6)"; color = "#14532d"; label = "Added"; }
                    else if (isReplace)  { bg = "rgba(251,191,36,0.12)"; border = "1px solid rgba(251,191,36,0.55)"; color = "#92400E"; label = "Replace"; }
                    else { bg = appTheme.pillBackground; border = appTheme.pillBorder; color = appTheme.primaryText; label = mealType === "breakfast" ? "Bfast" : mealType === "lunch" ? "Lunch" : "Dinner"; }
                    return (
                      <button key={mealType} onClick={() => { if (isPast || isAdded) return; setQuickAddSlots((prev) => isSelected ? prev.filter((s) => !(s.dateKey === dk && s.mealType === mealType)) : [...prev, { dateKey: dk, mealType }]); }} style={{ padding: "8px 4px", borderRadius: "10px", border, background: bg, color, fontWeight: isSelected ? "700" : "600", fontSize: "11px", cursor: isPast || isAdded ? "default" : "pointer", transition: "all 0.15s ease", textAlign: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        {quickAddSlots.length > 0 && (
          <div style={{ textAlign: "right", padding: "10px 20px 0" }}>
            <button onClick={() => setQuickAddSlots([])} style={{ background: "none", border: "none", cursor: "pointer", color: "#DC2626", fontSize: "13px", fontWeight: "500", fontFamily: appTheme.fontBody, padding: "4px 8px" }}>Clear selection</button>
          </div>
        )}
        <div style={{ padding: "8px 20px 0", fontSize: "11px", color: appTheme.mutedText, fontFamily: appTheme.fontBody }}>Adjusts shopping list quantities</div>
        <div style={{ padding: "10px 20px 20px", borderTop: "1px solid rgba(0,0,0,0.06)", flexShrink: 0, display: "flex", gap: "8px", alignItems: "stretch" }}>
          <div style={{ display: "flex", alignItems: "center", borderRadius: "14px", border: "none", background: appTheme.accentColor, flexShrink: 0, overflow: "hidden" }}>
            <button onClick={() => setCurrentRecipeServings((s) => Math.max(1, s - 1))} disabled={currentRecipeServings <= 1} style={{ width: "32px", height: "100%", border: "none", background: "transparent", color: "#ffffff", fontSize: "20px", fontWeight: "300", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1, opacity: currentRecipeServings <= 1 ? 0.3 : 1, pointerEvents: currentRecipeServings <= 1 ? "none" : "auto" }}>−</button>
            <span style={{ fontSize: "12px", fontWeight: "700", color: "#ffffff", padding: "0 2px", minWidth: "54px", textAlign: "center", whiteSpace: "nowrap" }}>Serves {currentRecipeServings}</span>
            <button onClick={() => setCurrentRecipeServings((s) => Math.min(2, s + 1))} disabled={currentRecipeServings >= 2} style={{ width: "32px", height: "100%", border: "none", background: "transparent", color: "#ffffff", fontSize: "20px", fontWeight: "300", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1, opacity: currentRecipeServings >= 2 ? 0.3 : 1, pointerEvents: currentRecipeServings >= 2 ? "none" : "auto" }}>+</button>
          </div>
          <button onClick={() => { if (!quickAddSlots.length || !setPlan || !setPlanToastMessage) return; setPlan((prev) => { const updated = { ...prev }; quickAddSlots.forEach(({ dateKey: dk2, mealType }) => { updated[dk2] = { ...updated[dk2], [mealType]: { ...selectedRecipe, servingsOverride: currentRecipeServings } }; }); return updated; }); setPlanToastMessage(quickAddSlots.length === 1 ? `${selectedRecipe.name} added to ${quickAddSlots[0].mealType}` : `${selectedRecipe.name} added to ${quickAddSlots.length} slots`); setQuickAddSlots([]); setShowQuickAdd(false); setQuickAddWeekOffset(0); closeRecipeModal(); }} style={{ flex: 1, padding: "13px", borderRadius: "14px", border: appTheme.accentBorder, background: quickAddSlots.length > 0 ? appTheme.accentColor : appTheme.accentLight, color: quickAddSlots.length > 0 ? "#ffffff" : appTheme.accentColor, fontWeight: "700", fontSize: "14px", cursor: quickAddSlots.length > 0 ? "pointer" : "default", opacity: quickAddSlots.length > 0 ? 1 : 0.5, transition: "all 0.15s ease" }}>
            {quickAddSlots.length === 0 ? "Confirm" : quickAddSlots.length === 1 ? "Confirm 1 slot" : `Confirm ${quickAddSlots.length} slots`}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  // ── FULL-PAGE HERO LAYOUT ──────────────────────────────────────────────────
  if (fullPage) {
    const floatingBtn = {
      width: "32px", height: "32px", borderRadius: "999px",
      background: "rgba(0,0,0,0.35)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
      border: "none",
      color: "#ffffff", cursor: "pointer",
      display: "flex", alignItems: "center", justifyContent: "center",
      flexShrink: 0, padding: 0,
    };


    return (
      <>
        <div style={{ position: "fixed", inset: 0, zIndex: 1500, overflow: "hidden", background: appTheme.pageBackground }}>

          {/* ── HERO ── */}
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "72vh" }}>

            {/* Background image or placeholder */}
            {selectedRecipe.image ? (
              <img
                src={selectedRecipe.image}
                alt={selectedRecipe.name}
                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              />
            ) : (
              <div style={{ width: "100%", height: "100%", background: `url(${recipePlaceholderBg}) center/cover no-repeat` }} />
            )}

            {/* Top gradient — for text + header legibility */}
            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(0,0,0,0.68) 0%, transparent 55%)", pointerEvents: "none" }} />

            {/* Bottom gradient — for macro bar legibility */}
            <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: "55%", background: "linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 100%)", pointerEvents: "none" }} />

            {/* ── Floating header ── */}
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, paddingTop: "env(safe-area-inset-top, 0px)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 16px" }}>

                {/* Back */}
                <button onClick={closeRecipeModal} style={{ ...floatingBtn, fontSize: "20px", lineHeight: 1, fontWeight: "300" }}>‹</button>

                {/* Right icons */}
                <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>

                  {/* Share */}
                  <div style={{ position: "relative" }}>
                    <button onClick={handleShare} style={floatingBtn}>
                      <svg width="14" height="18" viewBox="0 0 17 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M16.5 9.00059V18.0006C16.5 18.3984 16.342 18.7799 16.0607 19.0612C15.7794 19.3426 15.3978 19.5006 15 19.5006H1.5C1.10218 19.5006 0.720644 19.3426 0.43934 19.0612C0.158035 18.7799 0 18.3984 0 18.0006V9.00059C0 8.60277 0.158035 8.22123 0.43934 7.93993C0.720644 7.65863 1.10218 7.50059 1.5 7.50059H3.75C3.94891 7.50059 4.13968 7.57961 4.28033 7.72026C4.42098 7.86091 4.5 8.05168 4.5 8.25059C4.5 8.4495 4.42098 8.64027 4.28033 8.78092C4.13968 8.92157 3.94891 9.00059 3.75 9.00059H1.5V18.0006H15V9.00059H12.75C12.5511 9.00059 12.3603 8.92157 12.2197 8.78092C12.079 8.64027 12 8.4495 12 8.25059C12 8.05168 12.079 7.86091 12.2197 7.72026C12.3603 7.57961 12.5511 7.50059 12.75 7.50059H15C15.3978 7.50059 15.7794 7.65863 16.0607 7.93993C16.342 8.22123 16.5 8.60277 16.5 9.00059ZM5.03063 5.03122L7.5 2.5609V11.2506C7.5 11.4495 7.57902 11.6403 7.71967 11.7809C7.86032 11.9216 8.05109 12.0006 8.25 12.0006C8.44891 12.0006 8.63968 11.9216 8.78033 11.7809C8.92098 11.6403 9 11.4495 9 11.2506V2.5609L11.4694 5.03122C11.6101 5.17195 11.801 5.25101 12 5.25101C12.199 5.25101 12.3899 5.17195 12.5306 5.03122C12.6714 4.89048 12.7504 4.69961 12.7504 4.50059C12.7504 4.30157 12.6714 4.1107 12.5306 3.96997L8.78063 0.219965C8.71097 0.150233 8.62825 0.0949133 8.53721 0.0571701C8.44616 0.0194269 8.34856 0 8.25 0C8.15144 0 8.05384 0.0194269 7.96279 0.0571701C7.87175 0.0949133 7.78903 0.150233 7.71937 0.219965L3.96937 3.96997C3.82864 4.1107 3.74958 4.30157 3.74958 4.50059C3.74958 4.69961 3.82864 4.89048 3.96938 5.03121C4.11011 5.17195 4.30098 5.25101 4.5 5.25101C4.69902 5.25101 4.88989 5.17195 5.03063 5.03122Z" fill="white"/>
                      </svg>
                    </button>
                    {showCopiedToast && (
                      <div style={{ position: "absolute", top: "44px", right: 0, background: "rgba(0,0,0,0.75)", color: "#fff", fontSize: "11px", fontWeight: "600", padding: "4px 8px", borderRadius: "8px", whiteSpace: "nowrap" }}>Link copied!</div>
                    )}
                  </div>

                  {/* Bookmark */}
                  <button onClick={() => toggleFavourite?.(selectedRecipe?.name)} style={floatingBtn}>
                    <BookmarkSimple
                      weight={favouriteRecipes?.includes(selectedRecipe?.name) ? "fill" : "bold"}
                      color="#ffffff"
                      size={18}
                    />
                  </button>

                  {/* Edit (custom recipes only) */}
                  {isCustomRecipe && (
                    <button onClick={handleEditSelectedRecipe} style={floatingBtn}>
                      <PencilSimple size={18} color="#ffffff" weight="bold" />
                    </button>
                  )}

                  {/* Three-dots menu (plan context only) */}
                  {fpDateKey && fpMealType && (
                    <div style={{ position: "relative" }}>
                      <button onClick={() => setShowMenu((p) => !p)} style={{ ...floatingBtn, fontSize: "16px", letterSpacing: "0.05em" }}>⋮</button>
                      {showMenu && (
                        <>
                          <div onClick={() => setShowMenu(false)} style={{ position: "fixed", inset: 0, zIndex: 4 }} />
                          <div style={{ position: "absolute", top: "44px", right: 0, zIndex: 5, width: "180px", borderRadius: "14px", background: appTheme.accentColor, boxShadow: "0 8px 24px rgba(15,23,42,0.18)", overflow: "hidden" }}>
                            {!fullPageContext?.isProgramme && openManualEntryModal && (
                              <>
                                <button onClick={() => { openManualEntryModal(fpDateKey, "ate-different", fpMealType); setShowMenu(false); closeRecipeModal(); }} style={{ width: "100%", padding: "12px 14px", border: "none", background: "transparent", color: "#ffffff", fontSize: "13px", fontWeight: "600", textAlign: "left", cursor: "pointer" }}>Ate something else</button>
                                <div style={{ height: "1px", background: "rgba(255,255,255,0.18)", margin: "0 14px" }} />
                              </>
                            )}
                            <button onClick={() => { openMealSlotSheet?.(fpDateKey, fpMealType); setShowMenu(false); closeRecipeModal(); }} style={{ width: "100%", padding: "12px 14px", border: "none", background: "transparent", color: "#ffffff", fontSize: "13px", fontWeight: "600", textAlign: "left", cursor: "pointer" }}>Swap meal</button>
                            {!fullPageContext?.isProgramme && (
                              <>
                                <div style={{ height: "1px", background: "rgba(255,255,255,0.18)", margin: "0 14px" }} />
                                <button onClick={() => { removeFromPlan?.(fpDateKey, fpMealType); setShowMenu(false); closeRecipeModal(); }} style={{ width: "100%", padding: "12px 14px", border: "none", background: "transparent", color: "rgba(255,180,180,1)", fontSize: "13px", fontWeight: "600", textAlign: "left", cursor: "pointer" }}>Remove</button>
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}

                </div>
              </div>
            </div>

            {/* ── Recipe info overlay (below floating header) ── */}
            <div style={{
              position: "absolute", top: 0, left: 0, right: 0,
              padding: "0 16px",
              paddingTop: "calc(env(safe-area-inset-top, 0px) + 56px)",
              pointerEvents: "none",
            }}>
              <h2 style={{ color: "#ffffff", fontFamily: appTheme.fontHeading, fontWeight: "600", fontSize: isMobile ? "24px" : "28px", margin: "0 0 6px", lineHeight: 1.2 }}>
                {selectedRecipe.name}
              </h2>
              {hasDescription && (
                <p style={{ color: "rgba(255,255,255,0.85)", fontSize: "13px", fontFamily: appTheme.fontBody, margin: 0, lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                  {selectedRecipe.description}
                </p>
              )}
            </div>

            {/* ── Pills + macro bar stacked at bottom of hero ── */}
            <div style={{ position: "absolute", bottom: "calc(10vh + 16px)", left: "12px", right: "12px", zIndex: 1, pointerEvents: "none", display: "flex", flexDirection: "column", gap: "10px" }}>
              {/* Time, calorie and tag pills */}
              {(() => {
                const darkPill = {
                  padding: "5px 11px", borderRadius: "999px", fontSize: "12px", fontWeight: "500",
                  background: "rgba(0,0,0,0.5)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
                  color: "#ffffff", display: "inline-flex", alignItems: "center", gap: "4px",
                };
                const firstTag = selectedRecipe.tags?.[0];
                const tagColor = firstTag ? (TAG_COLORS[firstTag] || null) : null;
                const tagLabel = firstTag ? firstTag.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase()) : null;
                return (hasTime || hasCalories || firstTag) ? (
                  <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "6px" }}>
                    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                      {hasTime     && <span style={darkPill}><svg width="11" height="14" viewBox="0 0 18 21" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}><path d="M9 3C7.21997 3 5.47991 3.52784 3.99987 4.51677C2.51983 5.50571 1.36628 6.91131 0.685088 8.55585C0.00389957 10.2004 -0.17433 12.01 0.172937 13.7558C0.520204 15.5016 1.37737 17.1053 2.63604 18.364C3.89472 19.6226 5.49836 20.4798 7.24419 20.8271C8.99002 21.1743 10.7996 20.9961 12.4442 20.3149C14.0887 19.6337 15.4943 18.4802 16.4832 17.0001C17.4722 15.5201 18 13.78 18 12C17.9973 9.61389 17.0482 7.32629 15.361 5.63905C13.6737 3.95182 11.3861 3.00273 9 3ZM9 19.5C7.51664 19.5 6.0666 19.0601 4.83323 18.236C3.59986 17.4119 2.63856 16.2406 2.07091 14.8701C1.50325 13.4997 1.35473 11.9917 1.64411 10.5368C1.9335 9.08197 2.64781 7.74559 3.6967 6.6967C4.7456 5.64781 6.08197 4.9335 7.53683 4.64411C8.99168 4.35472 10.4997 4.50325 11.8701 5.0709C13.2406 5.63856 14.4119 6.59986 15.236 7.83322C16.0601 9.06659 16.5 10.5166 16.5 12C16.4978 13.9884 15.7069 15.8948 14.3008 17.3008C12.8948 18.7069 10.9884 19.4978 9 19.5ZM13.2806 7.71938C13.3504 7.78903 13.4057 7.87175 13.4434 7.9628C13.4812 8.05384 13.5006 8.15144 13.5006 8.25C13.5006 8.34856 13.4812 8.44616 13.4434 8.53721C13.4057 8.62825 13.3504 8.71097 13.2806 8.78063L9.53063 12.5306C9.46095 12.6003 9.37822 12.6556 9.28718 12.6933C9.19613 12.731 9.09855 12.7504 9 12.7504C8.90146 12.7504 8.80388 12.731 8.71283 12.6933C8.62179 12.6556 8.53906 12.6003 8.46938 12.5306C8.3997 12.4609 8.34442 12.3782 8.30671 12.2872C8.269 12.1961 8.24959 12.0985 8.24959 12C8.24959 11.9015 8.269 11.8039 8.30671 11.7128C8.34442 11.6218 8.3997 11.5391 8.46938 11.4694L12.2194 7.71938C12.289 7.64964 12.3718 7.59432 12.4628 7.55658C12.5538 7.51884 12.6514 7.49941 12.75 7.49941C12.8486 7.49941 12.9462 7.51884 13.0372 7.55658C13.1283 7.59432 13.211 7.64964 13.2806 7.71938ZM6 0.75C6 0.551088 6.07902 0.360322 6.21967 0.21967C6.36033 0.0790176 6.55109 0 6.75 0H11.25C11.4489 0 11.6397 0.0790176 11.7803 0.21967C11.921 0.360322 12 0.551088 12 0.75C12 0.948912 11.921 1.13968 11.7803 1.28033C11.6397 1.42098 11.4489 1.5 11.25 1.5H6.75C6.55109 1.5 6.36033 1.42098 6.21967 1.28033C6.07902 1.13968 6 0.948912 6 0.75Z" fill="white"/></svg> {selectedRecipe.time} min</span>}
                      {hasCalories && <span style={darkPill}><svg width="11" height="14" viewBox="0 0 17 21" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}><path d="M9.69188 0.173286C9.59942 0.0964175 9.48984 0.0429048 9.37237 0.017259C9.2549 -0.00838687 9.13298 -0.00541401 9.0169 0.0259268C8.90082 0.0572676 8.79398 0.116058 8.70538 0.197341C8.61678 0.278624 8.54902 0.380017 8.50781 0.492973L6.44531 6.15641L4.18031 3.96172C4.10425 3.88795 4.01341 3.83113 3.91379 3.79501C3.81417 3.75889 3.70802 3.74429 3.60235 3.75217C3.49667 3.76005 3.39387 3.79024 3.30071 3.84074C3.20755 3.89124 3.12615 3.96091 3.06188 4.04516C1.03125 6.70579 0 9.38235 0 11.9998C0 14.1879 0.869194 16.2863 2.41637 17.8335C3.96354 19.3807 6.06196 20.2498 8.25 20.2498C10.438 20.2498 12.5365 19.3807 14.0836 17.8335C15.6308 16.2863 16.5 14.1879 16.5 11.9998C16.5 6.42641 11.7384 1.87485 9.69188 0.173286ZM8.25 18.7498C6.4604 18.7479 4.74466 18.0361 3.47922 16.7706C2.21378 15.5052 1.50199 13.7894 1.5 11.9998C1.5 9.93735 2.25844 7.80079 3.75562 5.63985L6.22781 8.03797C6.3176 8.12517 6.42777 8.18851 6.5483 8.22222C6.66883 8.25593 6.79588 8.25895 6.91787 8.23099C7.03986 8.20303 7.15292 8.14499 7.24674 8.06215C7.34055 7.97931 7.41215 7.87431 7.455 7.75672L9.54281 2.03141C11.6447 3.94485 15 7.67891 15 11.9998C14.998 13.7894 14.2862 15.5052 13.0208 16.7706C11.7553 18.0361 10.0396 18.7479 8.25 18.7498Z" fill="#FFAA2C"/><path d="M11.8172 15.2473C11.8355 15.2108 11.8476 15.1737 11.8522 15.1392C11.8569 15.1047 11.8541 15.0738 11.844 15.0494L11.3764 13.9036L12.8993 12.1443C12.9345 12.1034 12.9626 12.0551 12.9791 12.0068C12.9957 11.9585 12.9999 11.9128 12.9911 11.8766C12.9823 11.8404 12.961 11.8156 12.9304 11.806C12.8998 11.7965 12.8615 11.8026 12.8214 11.8236L9.02709 13.8111C8.99656 13.8269 8.96601 13.8506 8.93816 13.8802C8.91031 13.9099 8.88603 13.9445 8.8675 13.981C8.84896 14.0175 8.83675 14.0548 8.83194 14.0895C8.82714 14.1241 8.82989 14.1551 8.83996 14.1797L9.30781 15.3257L7.78695 17.0833C7.7517 17.1243 7.72368 17.1725 7.70712 17.2208C7.69056 17.2691 7.68636 17.3148 7.69515 17.351C7.70394 17.3873 7.72525 17.412 7.75587 17.4216C7.78648 17.4311 7.82473 17.425 7.86485 17.4041L11.6592 15.4165C11.6894 15.4006 11.7197 15.3769 11.7472 15.3473C11.7748 15.3178 11.7988 15.2835 11.8172 15.2473ZM8.53587 16.6625L9.62363 15.4058C9.66226 15.3613 9.69225 15.3081 9.70825 15.2556C9.72425 15.2032 9.72523 15.1549 9.71101 15.1195L9.28269 14.0679L12.1493 12.5663L11.0617 13.8231C11.0231 13.8676 10.9931 13.9209 10.9771 13.9733C10.9611 14.0257 10.9601 14.074 10.9743 14.1094L11.403 15.1594L8.53587 16.6625Z" fill="#E2A619"/></svg> {selectedRecipe.calories} kcal</span>}
                    </div>
                    {tagLabel && tagColor && <span style={{ ...darkPill, background: tagColor }}>{tagLabel}</span>}
                  </div>
                ) : null;
              })()}
              {/* Macro bar */}
              {macroBar}
            </div>
          </div>{/* end hero */}

          {/* ── DRAGGABLE SHEET ── */}
          <div
            ref={sheetRef}
            style={{
              position: "absolute",
              top: 0, left: 0, right: 0,
              height: `calc(100% - ${actionBarHeight}px)`,
              transform: `translateY(${peekRef.current}px)`,
              borderRadius: "24px 24px 0 0",
              background: appTheme.pageBackground,
              boxShadow: "0 -4px 32px rgba(0,0,0,0.15)",
              display: "flex",
              flexDirection: "column",
              zIndex: 2,
              willChange: "transform",
            }}
          >
            {/* Combined drag zone: handle pill + tab bar */}
            <div
              {...bind()}
              style={{
                touchAction: "none", userSelect: "none",
                cursor: "grab", flexShrink: 0,
              }}
            >
              {/* Drag handle pill */}
              <div style={{ paddingTop: "12px", paddingBottom: "12px", display: "flex", justifyContent: "center" }}>
                <div style={{ width: "48px", height: "5px", borderRadius: "999px", background: "rgba(0,0,0,0.2)" }} />
              </div>
              {/* Tab bar — taps still fire via filterTaps */}
              {tabBar}
            </div>

            {/* Scrollable tab content */}
            <div
              ref={scrollRef}
              {...bindScroll()}
              className="modal-scroll-hidden"
              style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch", overscrollBehavior: "contain" }}
            >
              {tabContent}
            </div>
          </div>{/* end sheet */}

          {/* ── BOTTOM ACTION BAR — always above sheet ── */}
          <div
            ref={actionBarRef}
            style={{
              position: "absolute", bottom: 0, left: 0, right: 0,
              zIndex: 3,
              background: appTheme.pageBackground,
              borderTop: "1px solid rgba(0,0,0,0.06)",
              padding: "12px 20px",
              paddingBottom: "calc(12px + env(safe-area-inset-bottom, 0px))",
              display: "flex", flexDirection: "column", gap: "10px",
            }}
          >
            {allowQuickAdd ? (
              <div style={{ display: "flex", gap: "8px", alignItems: "stretch" }}>
                <button onClick={() => { setShowQuickAdd(true); setQuickAddSlots([]); }} style={{ flex: 1, padding: "12px", borderRadius: "14px", border: "none", background: appTheme.accentColor, color: "#ffffff", fontWeight: "700", fontSize: "14px", cursor: "pointer", transition: "all 0.15s ease" }}>+ Add to Plan</button>
                <button onClick={onStartCooking} style={{ width: "46px", flexShrink: 0, padding: "12px", borderRadius: "14px", border: "none", background: appTheme.accentColor, color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 13.87A4 4 0 0 1 7.41 6a5.11 5.11 0 0 1 1.05-1.54 5 5 0 0 1 7.08 0A5.11 5.11 0 0 1 16.59 6 4 4 0 0 1 18 13.87V21H6Z"/>
                    <line x1="6" y1="17" x2="18" y2="17"/>
                  </svg>
                </button>
              </div>
            ) : mealSlotSheetTarget ? (
              <button onClick={() => { applyMealToSlot(selectedRecipe); closeRecipeModal(); }} style={{ width: "100%", padding: "12px", borderRadius: "14px", border: appTheme.accentBorder, background: appTheme.accentColor, color: "#fff", fontWeight: "700", fontSize: "14px", cursor: "pointer" }}>Confirm ✓</button>
            ) : (
              <div style={{ display: "flex", gap: "8px", alignItems: "stretch" }}>
                <button onClick={onStartCooking} style={{ flex: 1, padding: "14px", borderRadius: "14px", border: "none", background: appTheme.accentColor, color: "#fff", fontWeight: "700", fontSize: "15px", cursor: "pointer" }}>Start Cooking</button>
                {fpDateKey && fpMealType && completeMeal && (
                  <button
                    onClick={() => completeMeal(fpDateKey, fpMealType)}
                    style={{ width: "52px", flexShrink: 0, padding: "14px", borderRadius: "14px", border: "none", background: isCooked ? "rgba(220,252,231,0.9)" : appTheme.accentLight, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.18s ease" }}
                  >
                    {isCooked ? (
                      <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                        <circle cx="11" cy="11" r="10" fill="rgba(34,197,94,0.85)"/>
                        <path d="M6 11l3.5 3.5 7-7" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                        <circle cx="11" cy="11" r="10" stroke={appTheme.accentColor} strokeWidth="1.8" fill="none"/>
                      </svg>
                    )}
                  </button>
                )}
              </div>
            )}
          </div>

        </div>{/* end fixed container */}

        {quickAddSheet}
      </>
    );
  }

  // ── MODAL OVERLAY LAYOUT (HomeDayModal context — unchanged) ─────────────────
  const outerStyle = modalOverlayStyle;
  const innerStyle = {
    ...modalContentStyle,
    maxWidth: isMobile ? "94vw" : "480px",
    width: "100%",
    padding: "0",
    maxHeight: isMobile ? "90vh" : "calc(100vh - 40px)",
    overflowY: "hidden",
    overflowX: "hidden",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    borderRadius: "24px",
  };

  return (
    <>
    <div style={outerStyle} onClick={closeRecipeModal}>
      <div className="modal-scroll-hidden" style={innerStyle} onClick={(e) => e.stopPropagation()}>

        {/* Top nav bar */}
        <div style={{ display: "flex", alignItems: "center", padding: "14px 18px 10px", background: appTheme.pageBackground, flexShrink: 0, gap: "8px" }}>
          <button onClick={closeRecipeModal} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "24px", color: appTheme.primaryText, padding: "0 8px 0 0", lineHeight: 1, fontWeight: "300", flexShrink: 0 }}>‹</button>
          <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
            {activeTab !== "ingredients" && (
              <span style={{ fontWeight: "600", fontSize: "15px", color: appTheme.primaryText, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "220px" }}>{selectedRecipe.name}</span>
            )}
          </div>
          <div style={{ display: "flex", gap: "4px", alignItems: "center", flexShrink: 0 }}>
            <div style={{ position: "relative" }}>
              <button onClick={handleShare} style={{ background: "none", border: "none", cursor: "pointer", padding: "6px", lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="17" height="20" viewBox="0 0 17 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16.5 9.00059V18.0006C16.5 18.3984 16.342 18.7799 16.0607 19.0612C15.7794 19.3426 15.3978 19.5006 15 19.5006H1.5C1.10218 19.5006 0.720644 19.3426 0.43934 19.0612C0.158035 18.7799 0 18.3984 0 18.0006V9.00059C0 8.60277 0.158035 8.22123 0.43934 7.93993C0.720644 7.65863 1.10218 7.50059 1.5 7.50059H3.75C3.94891 7.50059 4.13968 7.57961 4.28033 7.72026C4.42098 7.86091 4.5 8.05168 4.5 8.25059C4.5 8.4495 4.42098 8.64027 4.28033 8.78092C4.13968 8.92157 3.94891 9.00059 3.75 9.00059H1.5V18.0006H15V9.00059H12.75C12.5511 9.00059 12.3603 8.92157 12.2197 8.78092C12.079 8.64027 12 8.4495 12 8.25059C12 8.05168 12.079 7.86091 12.2197 7.72026C12.3603 7.57961 12.5511 7.50059 12.75 7.50059H15C15.3978 7.50059 15.7794 7.65863 16.0607 7.93993C16.342 8.22123 16.5 8.60277 16.5 9.00059ZM5.03063 5.03122L7.5 2.5609V11.2506C7.5 11.4495 7.57902 11.6403 7.71967 11.7809C7.86032 11.9216 8.05109 12.0006 8.25 12.0006C8.44891 12.0006 8.63968 11.9216 8.78033 11.7809C8.92098 11.6403 9 11.4495 9 11.2506V2.5609L11.4694 5.03122C11.6101 5.17195 11.801 5.25101 12 5.25101C12.199 5.25101 12.3899 5.17195 12.5306 5.03122C12.6714 4.89048 12.7504 4.69961 12.7504 4.50059C12.7504 4.30157 12.6714 4.1107 12.5306 3.96997L8.78063 0.219965C8.71097 0.150233 8.62825 0.0949133 8.53721 0.0571701C8.44616 0.0194269 8.34856 0 8.25 0C8.15144 0 8.05384 0.0194269 7.96279 0.0571701C7.87175 0.0949133 7.78903 0.150233 7.71937 0.219965L3.96937 3.96997C3.82864 4.1107 3.74958 4.30157 3.74958 4.50059C3.74958 4.69961 3.82864 4.89048 3.96938 5.03121C4.11011 5.17195 4.30098 5.25101 4.5 5.25101C4.69902 5.25101 4.88989 5.17195 5.03063 5.03122Z" fill={appTheme.secondaryText}/>
                </svg>
              </button>
              {showCopiedToast && (
                <div style={{ position: "absolute", bottom: "-28px", right: 0, background: appTheme.primaryText, color: appTheme.pageBackground.includes("linear") ? "#FAF3E8" : "#fff", fontSize: "11px", fontWeight: "600", padding: "4px 8px", borderRadius: "8px", whiteSpace: "nowrap", pointerEvents: "none" }}>Link copied!</div>
              )}
            </div>
            <button onClick={() => toggleFavourite?.(selectedRecipe?.name)} style={{ background: "none", border: "none", cursor: "pointer", padding: "6px", lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <BookmarkSimple
                weight={favouriteRecipes?.includes(selectedRecipe?.name) ? "fill" : "bold"}
                color={favouriteRecipes?.includes(selectedRecipe?.name) ? appTheme.accentColor : appTheme.secondaryText}
                size={22}
              />
            </button>
            {isCustomRecipe && (
              <button onClick={handleEditSelectedRecipe} style={{ background: "none", border: "none", cursor: "pointer", padding: "6px", lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <PencilSimple size={22} color={appTheme.secondaryText} weight="bold" />
              </button>
            )}
          </div>
        </div>

        {/* Scrollable body */}
        <div className="modal-scroll-hidden" style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}>
          {activeTab === "ingredients" && (
            <div style={{ padding: "4px 20px 0", background: appTheme.pageBackground }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "10px", marginBottom: "8px" }}>
                <h2 style={{ margin: 0, fontSize: isMobile ? "26px" : "30px", fontWeight: "500", fontFamily: appTheme.fontHeading, color: appTheme.primaryText, lineHeight: 1.15 }}>{selectedRecipe.name}</h2>
              </div>
              {hasDescription && <p style={{ margin: "0 0 14px", fontSize: "14px", color: appTheme.secondaryText, lineHeight: 1.65 }}>{selectedRecipe.description}</p>}
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "16px" }}>
                {hasTime     && <span style={pillStyle}>⏱ {selectedRecipe.time} min</span>}
                {hasCalories && <span style={pillStyle}>🔥 {selectedRecipe.calories} kcal</span>}
              </div>
              <div style={{ position: "relative", marginBottom: "4px" }}>
                {selectedRecipe.image ? (
                  <div style={{ borderRadius: "20px", overflow: "hidden", height: isMobile ? "320px" : "380px" }}>
                    <img src={selectedRecipe.image} alt={selectedRecipe.name} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  </div>
                ) : (
                  <div style={{ borderRadius: "20px", height: isMobile ? "160px" : "200px", background: `url(${recipePlaceholderBg}) center/cover no-repeat` }} />
                )}
                {macroBar && <div style={{ position: "absolute", bottom: "14px", left: "12px", right: "12px", zIndex: 1 }}>{macroBar}</div>}
              </div>
            </div>
          )}

          {tabBar}
          {tabContent}
        </div>


      </div>
    </div>
    {quickAddSheet}
    </>
  );
}

export default RecipeDetailsModal;
